import { InjectionToken, Type } from '@angular/core';

/**
 * One widget the app knows how to render.
 *
 * - `key` is the value an editor picks in the Storyblok `type` dropdown on
 *   a `widget` block. It's how the right component gets matched.
 * - `loader` is called the first time that widget is rendered on a page. It
 *   returns the component class via a dynamic `import()`, which keeps each
 *   widget's code in its own JS chunk — pages without the widget never
 *   download it.
 */
export interface WidgetEntry {
    readonly key: string;
    readonly loader: () => Promise<Type<unknown>>;
}

/**
 * The list of widgets `WidgetComponent` can render. Registered once at the
 * app root through `provideWidgets()`.
 *
 * Deliberately has no default value. If `provideWidgets()` is missing from
 * the app's providers, `inject(WIDGETS)` throws `NullInjectorError` and the
 * stack trace points straight at the missing setup. A `[]` default would
 * instead let the app boot, then later log "Unknown widget type" at the
 * first `<ur-widget>` on a page — which reads like a CMS data problem and
 * sends people debugging the wrong thing.
 */
export const WIDGETS = new InjectionToken<readonly WidgetEntry[]>('WIDGETS');
